import { StackWithDividers } from '@components'

import { Divider, Flex, Icon, Space, Spacing, Text } from '@billy/design-system'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoicingSettingsLoadErrorMessage } from '../../../../../../elements/InvoicingSettingsLoadErrorMessage'
import { ComplianceSettingsFormFieldsSkeletons } from './elements/ComplianceSettingsFormFieldsSkeletons'
import { LegalMentions } from './elements/LegalMentions'
import { LegalSettings } from './elements/LegalSettings'
import * as Styled from './styles'

interface ComplianceSettingsFormFieldsProps {
  hasFailedToLoad: boolean
  isLoading: boolean
  onRetry: () => Promise<unknown>
}

// The fields stay hidden until the settings document has loaded. Showing them on a failed fetch
// would let an edit mark the form dirty, skip hydration, and save the defaults.
export const ComplianceSettingsFormFields = ({
  hasFailedToLoad,
  isLoading,
  onRetry,
}: ComplianceSettingsFormFieldsProps): ReactElement => {
  const { t } = useTranslation()

  if (isLoading) {
    return <ComplianceSettingsFormFieldsSkeletons />
  }

  if (hasFailedToLoad) {
    return <InvoicingSettingsLoadErrorMessage onRetry={onRetry} />
  }

  return (
    <Flex flexDirection="column" gap={Spacing.S4}>
      <Text weight="medium">{t('settings.organization.external_invoice.legal_mentions.title')}</Text>
      <Styled.ComplianceSettingsFormFieldsWrapper>
        <StackWithDividers divider={<Divider />}>
          <LegalMentions />
          <LegalSettings />
        </StackWithDividers>
      </Styled.ComplianceSettingsFormFieldsWrapper>
      <Space size="s8" />
      <Flex alignItems="center" gap={Spacing.S8}>
        <Icon icon="eye" size="sm" />
        <Text colorVariant="discreet" variant="bodySM">
          {t('settings.organization.external_invoice.legal_mentions.note')}
        </Text>
      </Flex>
    </Flex>
  )
}
